import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CloseIcon } from "../Auth/onboarding/icons";
import { GripIcon, PlusIcon } from "./icons";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { useSelector } from "react-redux";
import { RoutesPaths } from "../../Router/constant";
import { isFreePlan } from "../../utils/plan";

const WELCOME_MAX = 120;

export default function BehaviorDisplayCard({ settings, onChange }) {
  const user = useSelector((state) => state.auth.user);
  const freePlan = isFreePlan(user);

  // Keep the saved value honest: a paid account that turns the badge off and
  // later drops to free would otherwise keep saving showPoweredBy: false while
  // the UI shows it locked on.
  useEffect(() => {
    if (freePlan && settings.showPoweredBy !== true) {
      onChange("showPoweredBy", true);
    }
  }, [freePlan, settings.showPoweredBy, onChange]);

  const updateChip = (index, value) => {
    const next = [...settings.quickActions];
    next[index] = value;
    onChange("quickActions", next);
  };

  const removeChip = (index) => {
    onChange(
      "quickActions",
      settings.quickActions.filter((_, i) => i !== index),
    );
  };

  const addChip = () => {
    onChange("quickActions", [...settings.quickActions, "New action"]);
  };

  return (
    <section className="dash-card cz-card">
      <h2 className="cz-card-title">5. Behavior &amp; display</h2>

      <div className="paddingBottom10">
        <label className="cz-field cz-welcome-field">
          <span className="cz-field-label">Welcome message</span>
          <div className="cz-textarea-wrap">
            <textarea
              rows={3}
              maxLength={WELCOME_MAX}
              value={settings.welcomeMessage}
              onChange={(e) => onChange("welcomeMessage", e.target.value)}
            />
            <span className="cz-char-count">
              {settings.welcomeMessage.length}/{WELCOME_MAX}
            </span>
          </div>
        </label>
      </div>

      <div className="cz-field cz-chips-field">
        <span className="cz-field-label">Quick action chips</span>
        <ul className="cz-chip-list">
          {settings.quickActions.map((chip, index) => (
            <li key={index}>
              <span className="cz-chip-grip">
                <GripIcon />
              </span>
              <input
                type="text"
                value={chip}
                onChange={(e) => updateChip(index, e.target.value)}
              />
              <button
                type="button"
                aria-label={`Remove ${chip}`}
                onClick={() => removeChip(index)}
              >
                <CloseIcon />
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="cz-add-chip" onClick={addChip}>
          <PlusIcon />
          Add another
        </button>

        <div className="cz-toggle-list">
          <label className="cz-toggle-row">
            <ToggleSwitch
              // Free plans always carry the badge, so it reads as on and
              // locked rather than off-but-unclickable.
              checked={freePlan ? true : settings.showPoweredBy}
              onChange={(v) => onChange("showPoweredBy", v)}
              label="Show Powered by Hostor"
              disabled={freePlan}
              title={
                freePlan
                  ? "Upgrade your plan to remove the Hostor badge"
                  : undefined
              }
            />
            <div className="cz-toggle-labelInner">
              Show Powered by Hostor
              {freePlan && (
                <Link to={RoutesPaths.BILLING} className="auth-inline-link">
                  Upgrade to remove
                </Link>
              )}
            </div>
          </label>
          <label className="cz-toggle-row">
            <ToggleSwitch
              checked={settings.showOnlineStatus}
              onChange={(v) => onChange("showOnlineStatus", v)}
              label="Show online status"
            />
            Show online status
          </label>
          <label className="cz-toggle-row">
            <ToggleSwitch
              checked={settings.isAuthorizationRequired}
              onChange={(v) => onChange("isAuthorizationRequired", v)}
              label="Require visitors to sign in"
            />
            <div className="cz-toggle-labelInner">
              Require visitors to sign in
              <span className="cz-toggle-hint">
                Visitors must identify themselves before they can chat.
              </span>
            </div>
          </label>
        </div>
      </div>
    </section>
  );
}
